'use client';

import { AnalyticsLinkViewed } from '@cc-shopping/platform-analytics/AnalyticsLinkViewed';
import { useAnalyticsPageView } from '@cc-shopping/platform-analytics/AnalyticsPageView/Context';
import { useAnalytics } from '@cc-shopping/platform-analytics/hooks/useAnalytics';
import { getCleanLocationHref } from '@cc-shopping/platform-analytics/lib/urlUtils';
import { slugify } from '@cc-shopping/utils/string/slugify';
import { Link as MuiLink } from '@mui/material';
import type { ReactNode } from 'react';
import { useRef } from 'react';
import { v4 as uuid } from 'uuid';

export type LinkClientProps = {
  href?: string;
  target?: string;
  rel?: string;
  linkCopy: string;
  children: ReactNode;
};

export const LinkClient = ({ href, target, rel, linkCopy, children }: LinkClientProps) => {
  const eventFiredRef = useRef<boolean>(false);
  const impressionId = useRef<string>(uuid());
  const { analytics, deployableName, deployableVersion, gitSha } = useAnalytics();
  const { pageViewId } = useAnalyticsPageView();

  const fireClickEvent = () => {
    if (analytics && !eventFiredRef.current) {
      eventFiredRef.current = true;
      void analytics.track(
        'link_clicked',
        {
          deployable_name: deployableName,
          deployable_version: deployableVersion,
          destination_url: href ?? '',
          entity_name: 'link',
          event_id: uuid(),
          link_copy: linkCopy,
          impression_pageview_id: pageViewId,
          impression_page_url: getCleanLocationHref(),
          parent_event_id: impressionId.current,
        },
        {
          context: {
            app: {
              name: deployableName,
              version: gitSha,
            },
          },
        },
      );
    }
  };

  return (
    <AnalyticsLinkViewed
      destinationUrl={href ?? ''}
      linkCopy={linkCopy}
      event_id={impressionId.current}
    >
      <MuiLink
        underline="hover"
        data-testid={`content-link-${slugify(linkCopy)}`}
        rel={rel}
        onClick={() => fireClickEvent()}
        href={href}
        target={target}
      >
        {children}
      </MuiLink>
    </AnalyticsLinkViewed>
  );
};
